import { drawLogicalImage } from './hd-assets';

export interface SpriteRect { x: number; y: number; width: number; height: number }
export interface SpriteDraw {
  image: CanvasImageSource;
  /** All rectangles use original Flash pixels, including HD atlas crops. */
  destination: SpriteRect;
  source?: SpriteRect;
}

function draw(ctx: CanvasRenderingContext2D, sprite: SpriteDraw, offsetX = 0, offsetY = 0): void {
  const d = sprite.destination;
  if (sprite.source) {
    const s = sprite.source;
    drawLogicalImage(ctx, sprite.image, s.x, s.y, s.width, s.height, d.x - offsetX, d.y - offsetY, d.width, d.height);
  } else drawLogicalImage(ctx, sprite.image, d.x - offsetX, d.y - offsetY, d.width, d.height);
}
function sameRect(a: SpriteRect | undefined, b: SpriteRect | undefined): boolean {
  return a === b || Boolean(a && b && a.x === b.x && a.y === b.y && a.width === b.width && a.height === b.height);
}

/** One small reusable surface per owner; no generated frames or new downloads. */
export class InterpolatedSprite {
  private surface: HTMLCanvasElement | null = null;
  private context: CanvasRenderingContext2D | null = null;

  draw(ctx: CanvasRenderingContext2D, current: SpriteDraw | null, next: SpriteDraw | null, mix: number): void {
    const amount = Math.max(0, Math.min(1, mix));
    if (!current && !next) return;
    if (amount === 0 || !next) { if (current) draw(ctx, current); return; }
    if (amount === 1 || !current) { if (next) draw(ctx, next); return; }
    if (current.image === next.image && sameRect(current.source, next.source)
      && sameRect(current.destination, next.destination)) { draw(ctx, current); return; }
    const a = current.destination, b = next.destination;
    const left = Math.min(a.x, b.x), top = Math.min(a.y, b.y);
    const width = Math.max(a.x + a.width, b.x + b.width) - left;
    const height = Math.max(a.y + a.height, b.y + b.height) - top;
    if (width <= 0 || height <= 0) return;
    this.surface ??= document.createElement('canvas');
    this.context ??= this.surface.getContext('2d');
    if (!this.context) { draw(ctx, current); return; }
    // Preserve HD detail at the canvas display scale while bounding scratch
    // memory. The final draw inherits only the caller's alpha/filter/shadow.
    const transform = ctx.getTransform();
    const scale = Math.max(1, Math.min(4, Math.hypot(transform.a, transform.b), Math.hypot(transform.c, transform.d)));
    const pixelWidth = Math.ceil(width * scale), pixelHeight = Math.ceil(height * scale);
    // Grow only: changing canvas dimensions for each differently sized aura
    // would reallocate its GPU backing store dozens of times every RAF.
    if (this.surface.width < pixelWidth) this.surface.width = pixelWidth;
    if (this.surface.height < pixelHeight) this.surface.height = pixelHeight;
    const buffer = this.context;
    buffer.setTransform(1, 0, 0, 1, 0, 0);
    buffer.clearRect(0, 0, pixelWidth, pixelHeight);
    buffer.setTransform(scale, 0, 0, scale, 0, 0);
    buffer.globalCompositeOperation = 'source-over';
    buffer.globalAlpha = 1 - amount; draw(buffer, current, left, top);
    // A weighted source-over pair darkens a fully opaque unchanged pixel.
    // Add premultiplied colors AND alpha in isolation, then source-over once.
    buffer.globalCompositeOperation = 'lighter';
    buffer.globalAlpha = amount; draw(buffer, next, left, top);
    buffer.globalAlpha = 1; buffer.globalCompositeOperation = 'source-over';
    ctx.drawImage(this.surface, 0, 0, pixelWidth, pixelHeight, left, top, pixelWidth / scale, pixelHeight / scale);
  }
}
